import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { StarRatingCriterion, StarRatingDimension, StarRatingValue } from 'constants/misc'
import { StarRatingDimensionState, StarRatingEntry } from 'models/assignment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import constate from 'constate'
import { logger } from 'utils/logging'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useSubmitStarRating } from 'dataQueries'
import { useTranslation } from 'react-i18next'

export interface StarRatingAnalyticsData {
  creativeId: string | null
  country: string | null
  workspaceId: string | null
  productKind: string | null
  productCategory: string | null
  orderId: string | null
  realtyId: string | null
}

export interface StarRatingPopupContextProps {
  assignmentId: string
  hasCreative: boolean
  isOpen: boolean
  creativeName?: string
  creativeAvatarUrl?: string
  initialRatings?: StarRatingEntry[]
  /** BE-reported favourite state, used as fallback when initialRatings has no CREATIVE entry */
  initialIsFavouriteCT?: boolean
  onComplete?: (entries: StarRatingEntry[], avgRating?: number | null) => void
  analyticsData?: StarRatingAnalyticsData
}

export enum StarRatingPopupStep {
  RATING = 'rating',
  THANK_YOU = 'thankyou'
}

// `dropCriteria` is used for VISUALS per ISSUE-1750 — legacy criteria from the BE are intentionally
// discarded on open so the next submit clears them. Do not "fix" by preserving initial.criteria.
const createInitialDimensionState = (
  initial?: StarRatingEntry,
  isFavoriteOverride?: boolean,
  dropCriteria = false
): StarRatingDimensionState => ({
  rating: initial?.rating ?? null,
  criteria: dropCriteria ? new Set<StarRatingCriterion>() : new Set(initial?.criteria ?? []),
  isFavorite: isFavoriteOverride ?? initial?.isFavorite ?? false,
  comment: initial?.ratingText ?? ''
})

export const [StarRatingPopupContextProvider, useStarRatingPopup] = constate(({
  assignmentId,
  hasCreative,
  isOpen,
  creativeName,
  creativeAvatarUrl,
  initialRatings,
  initialIsFavouriteCT,
  onComplete,
  analyticsData
}: StarRatingPopupContextProps) => {
  const { t } = useTranslation(['star_rating'])
  const { spawnErrorToast } = useSnackbar()
  const submitStarRating = useSubmitStarRating()

  const isUpdate = !!initialRatings?.length

  // BE may report isFavouriteCT=true even when there's no CREATIVE rating entry
  // yet — fall back to that flag so the favourite chip reflects the BE state.
  const resolvedInitialFavorite = initialIsFavouriteCT
    ?? initialRatings?.find(r => r.dimension === StarRatingDimension.CREATIVE)?.isFavorite
    ?? false

  // Step state
  const [step, setStep] = useState<StarRatingPopupStep>(StarRatingPopupStep.RATING)

  // Average rating returned from the API after submission
  const [responseAvgRating, setResponseAvgRating] = useState<number | null | undefined>(undefined)

  // Per-dimension form state
  const [dimensionStates, setDimensionStates] = useState<Record<StarRatingDimension, StarRatingDimensionState>>(() => ({
    [StarRatingDimension.VISUALS]: createInitialDimensionState(
      initialRatings?.find(r => r.dimension === StarRatingDimension.VISUALS),
      undefined,
      true
    ),
    [StarRatingDimension.CREATIVE]: createInitialDimensionState(
      initialRatings?.find(r => r.dimension === StarRatingDimension.CREATIVE),
      resolvedInitialFavorite
    )
  }))

  // Derive favourite toggle flags (toggle-based: compare current vs initial)
  const initialCreativeFavorite = useMemo(
    () => resolvedInitialFavorite,
    [resolvedInitialFavorite]
  )

  // Set rating for a dimension (only reset criteria if value changes).
  // For CREATIVE: when rating < 5, snap favorite back to the initial state
  // (clients can remove an existing favorite but not add a new one at <5);
  // when rating is 5, preserve the current favorite so toggling stars doesn't
  // wipe an already-favourited CT.
  const setDimensionRating = useCallback((dimension: StarRatingDimension, value: StarRatingValue) => {
    setDimensionStates(prev => {
      if (prev[dimension].rating === value) return prev
      const nextFavorite = dimension === StarRatingDimension.CREATIVE
        ? (value < 5 ? initialCreativeFavorite : prev[dimension].isFavorite)
        : false
      return {
        ...prev,
        [dimension]: {
          ...prev[dimension],
          rating: value,
          criteria: new Set<StarRatingCriterion>(),
          isFavorite: nextFavorite
        }
      }
    })
  }, [initialCreativeFavorite])

  // Toggle a criterion
  const toggleCriterion = useCallback((dimension: StarRatingDimension, criterion: StarRatingCriterion) => {
    setDimensionStates(prev => {
      const newCriteria = new Set(prev[dimension].criteria)
      if (newCriteria.has(criterion)) {
        newCriteria.delete(criterion)
      } else {
        newCriteria.add(criterion)
      }
      return {
        ...prev,
        [dimension]: {
          ...prev[dimension],
          criteria: newCriteria
        }
      }
    })
  }, [])

  // Toggle favorite
  const toggleFavorite = useCallback((dimension: StarRatingDimension) => {
    setDimensionStates(prev => ({
      ...prev,
      [dimension]: {
        ...prev[dimension],
        isFavorite: !prev[dimension].isFavorite
      }
    }))
  }, [])

  // Set comment for a dimension
  const setDimensionComment = useCallback((dimension: StarRatingDimension, value: string) => {
    setDimensionStates(prev => ({
      ...prev,
      [dimension]: {
        ...prev[dimension],
        comment: value
      }
    }))
  }, [])

  // Can submit: all active dimensions must have a rating
  const canSubmit = useMemo(() => {
    const visualsRated = dimensionStates[StarRatingDimension.VISUALS].rating !== null
    const creativeRated = !hasCreative || dimensionStates[StarRatingDimension.CREATIVE].rating !== null
    return visualsRated && creativeRated
  }, [dimensionStates, hasCreative])

  // Build entries from state
  const buildEntries = useCallback((): StarRatingEntry[] => {
    const entries: StarRatingEntry[] = []
    const visualsState = dimensionStates[StarRatingDimension.VISUALS]
    if (visualsState.rating !== null) {
      entries.push({
        dimension: StarRatingDimension.VISUALS,
        rating: visualsState.rating as StarRatingValue,
        criteria: Array.from(visualsState.criteria),
        isFavorite: visualsState.isFavorite,
        ratingText: visualsState.comment
      })
    }

    if (hasCreative) {
      const creativeState = dimensionStates[StarRatingDimension.CREATIVE]
      if (creativeState.rating !== null) {
        entries.push({
          dimension: StarRatingDimension.CREATIVE,
          rating: creativeState.rating as StarRatingValue,
          criteria: Array.from(creativeState.criteria),
          isFavorite: creativeState.isFavorite,
          ratingText: creativeState.comment
        })
      }
    }

    return entries
  }, [dimensionStates, hasCreative])

  // Submit handler
  const submit = useCallback(() => {
    if (!canSubmit || submitStarRating.isPending) return

    const creativeState = dimensionStates[StarRatingDimension.CREATIVE]
    const currentFavorite = creativeState.isFavorite

    const ratings = buildEntries().map(entry => ({
      type: entry.dimension,
      rating: entry.rating,
      ratingText: dimensionStates[entry.dimension].comment.trim(),
      criteria: entry.criteria
    }))

    submitStarRating.mutate(
      {
        assignmentId,
        ratings,
        addFavouriteCT: hasCreative && currentFavorite && !initialCreativeFavorite,
        removeFavouriteCT: hasCreative && !currentFavorite && initialCreativeFavorite
      },
      {
        onSuccess: ({ data }) => {
          const avgRating = data?.avgRating ?? null

          setResponseAvgRating(avgRating)
          setStep(StarRatingPopupStep.THANK_YOU)
        },
        onError: (error) => {
          logger.error('Star rating submission failed', error, { context: { component: 'StarRatingPopup', action: 'submit', metadata: { assignmentId } } })
          spawnErrorToast(t('star_rating:toast_rating_error'))
        }
      }
    )
  }, [canSubmit, buildEntries, dimensionStates, assignmentId, hasCreative, initialCreativeFavorite, submitStarRating, spawnErrorToast, t])

  // Reset state (for when popup is reopened)
  const reset = useCallback(() => {
    setStep(StarRatingPopupStep.RATING)
    setResponseAvgRating(undefined)
    setDimensionStates({
      [StarRatingDimension.VISUALS]: createInitialDimensionState(
        initialRatings?.find(r => r.dimension === StarRatingDimension.VISUALS),
        undefined,
        true
      ),
      [StarRatingDimension.CREATIVE]: createInitialDimensionState(
        initialRatings?.find(r => r.dimension === StarRatingDimension.CREATIVE),
        resolvedInitialFavorite
      )
    })
  }, [initialRatings, resolvedInitialFavorite])

  // Auto-reset when initialRatings content actually changes (not just the
  // reference).
  // Note: resolvedInitialFavorite is intentionally NOT part of the signature
  // — a favourite-only refetch while the popup is open should not wipe edits.
  const initialRatingsSignature = useMemo(
    () => JSON.stringify(initialRatings ?? []),
    [initialRatings]
  )

  useEffect(() => {
    if (step !== StarRatingPopupStep.THANK_YOU) {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRatingsSignature])

  // Track whether the popup was open so we can detect close transitions
  const wasOpenRef = useRef(false)

  // Reset when popup is reopened (e.g. after closing the thank-you step) + track open/close
  useEffect(() => {
    if (isOpen) {
      reset()
      logAnalyticsEvent(AnalyticsEvent.STAR_RATING_OPENED, {
        assignmentId,
        hasCreative,
        isUpdate,
        ...analyticsData
      })
    } else if (wasOpenRef.current) {
      logAnalyticsEvent(AnalyticsEvent.STAR_RATING_CLOSED, {
        assignmentId,
        hasCreative,
        isUpdate,
        wasSubmitted: step === StarRatingPopupStep.THANK_YOU,
        ...analyticsData
      })
    }
    wasOpenRef.current = isOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return {
    // State
    step,
    dimensionStates,
    isUpdate,
    isSubmitting: submitStarRating.isPending,
    canSubmit,
    hasCreative,
    creativeName,
    creativeAvatarUrl,
    responseAvgRating,

    // Actions
    setDimensionRating,
    setDimensionComment,
    toggleCriterion,
    toggleFavorite,
    submit,
    buildEntries,
    reset,
    onComplete
  }
})
